/**
 * ug-comp-countdown：仅处理倒计时逻辑。DOM 由 Liquid 输出（见 snippets/ug-comp-countdown.liquid）
 */
class UGCompCountdown extends HTMLElement {
  constructor() {
    super();
    this.loca_timezone = 8;
    this.set_coundown = null;
    this.now_date_time = null;
    this.now_date_timezone = null;
    this.countdownTime = null;
    this.timerId = null;
    this.initCallbackFn = null;
    this.stopAfterCallbackFn = null;
  }

  extractTimezoneHour(offset) {
    if (!offset) return 0;
    const match = offset.match(/^([+-])(\d{2})(\d{2})$/);
    if (!match) return 0;
    const sign = match[1] === '+' ? 1 : -1;
    const hours = parseInt(match[2], 10);
    const minutes = parseInt(match[3], 10);
    return sign * (hours + minutes / 60);
  }

  /**
   * 将 end 日期按 loca_timezone 解释为「该时区墙钟时间」的绝对时间戳（毫秒）。
   * - 主路径：RFC 3339 风格 + 数值时区（如 2026-01-01T00:00:00+08:00），与 iOS 13+ 一致行为。
   * - 回退：部分旧版 iOS / 非常规串若 Date 解析为 Invalid Date，用 Date.UTC(墙钟) - 时区小时偏移 计算，避免仅依赖 new Date(字符串)。
   */
  parseTargetToUTC(dateStr, timezoneHour) {
    const trimmed = (dateStr || '').trim();
    if (!trimmed) return NaN;
    const tzH = Number.isFinite(Number(timezoneHour)) ? Number(timezoneHour) : 0;
    const hasTime = trimmed.includes(':');
    let fullStr = hasTime ? trimmed.replace('T', ' ') : trimmed + ' 00:00:00';
    // 归一化空白（含 NBSP/全角空格），避免 iOS 上复制进的不可见字符导致 Date 解析失败
    fullStr = fullStr.replace(/[\s\u00a0\u3000]+/g, ' ');

    const sign = tzH >= 0 ? '+' : '-';
    const absH = String(Math.trunc(Math.abs(tzH))).padStart(2, '0');
    const minPart = Math.round((Math.abs(tzH) * 60) % 60);
    // 有非整点偏移（如 5.5）时，分钟部分写在偏移里，整点时为 :00
    const offMin = minPart > 0 ? `:${String(minPart).padStart(2, '0')}` : ':00';
    const firstSpace = fullStr.indexOf(' ');
    const localPart =
      firstSpace === -1 ? fullStr : fullStr.slice(0, firstSpace) + 'T' + fullStr.slice(firstSpace + 1);
    const isoStr = localPart + sign + absH + offMin;

    let ms = new Date(isoStr).getTime();
    if (!isNaN(ms)) return ms;

    // 与主路径等价的数值计算，不依赖 new Date(字符串)（iOS 部分异常输入时的兜底）
    const m = fullStr.match(
      /^(\d{4})-(\d{2})-(\d{2})[T\s]+(\d{1,2}):(\d{2}):(\d{2})/
    );
    if (!m) return NaN;
    const y = +m[1];
    const mo = +m[2];
    const d = +m[3];
    const h = +m[4];
    const mi = +m[5];
    const s = +m[6];
    ms = Date.UTC(y, mo - 1, d, h, mi, s) - tzH * 3600 * 1000;
    return ms;
  }

  calculateRemainingSeconds() {
    /** Liquid `now` | date: '%s' 已是 Unix 秒，表示绝对「此刻」，不要再减商店时区 */
    const nowMs = this.now_date_time * 1000;
    const endMs = this.parseTargetToUTC(this.set_coundown, this.loca_timezone);
    if (isNaN(endMs) || isNaN(nowMs)) {
      return 0;
    }
    return Math.floor((endMs - nowMs) / 1000);
  }

  connectedCallback() {
    const attrTz = this.getAttribute('loca_timezone');
    if (attrTz !== null && attrTz !== '') {
      this.loca_timezone = Number(attrTz);
    } else {
      this.loca_timezone = this.extractTimezoneHour(this.getAttribute('now_date_timezone'));
    }
    this.set_coundown = this.getAttribute('set_coundown')?.trim();
    this.now_date_time = Number(this.getAttribute('now_date_time'));
    this.now_date_timezone = this.getAttribute('now_date_timezone');

    if (!this.set_coundown || isNaN(this.now_date_time)) {
      return;
    }

    this.countdownTime = this.calculateRemainingSeconds();

    if (this.countdownTime > 0) {
      this.startCountdown();
    } else {
      this.stopCountdown();
    }

    setTimeout(() => {
      if (typeof this.initCallbackFn === 'function') {
        this.initCallbackFn();
      }
    }, 100);
  }

  startCountdown() {
    this.updateCountdown();
    this.timerId = setInterval(() => {
      this.countdownTime--;
      this.updateCountdown();
    }, 1000);
  }

  updateCountdown() {
    const surplus = this.countdownTime;

    if (surplus <= 0) {
      this.stopCountdown();
      return;
    }

    const container = this.querySelector('.ug-comp-countdown-div');
    if (container && container.classList.contains('dpn')) {
      container.classList.remove('dpn');
    }

    const days = Math.floor(surplus / 86400);
    const hours = Math.floor((surplus % 86400) / 3600);
    const minutes = Math.floor((surplus % 3600) / 60);
    const seconds = surplus % 60;

    this.updateUI(
      String(days).padStart(2, '0'),
      String(hours).padStart(2, '0'),
      String(minutes).padStart(2, '0'),
      String(seconds).padStart(2, '0')
    );
  }

  updateUI(serDays, serHour, serMin, serSec) {
    const daysEl = this.querySelector('.countdown-days');
    const hoursEl = this.querySelector('.countdown-hours');
    const minutesEl = this.querySelector('.countdown-minutes');
    const secondsEl = this.querySelector('.countdown-seconds');

    if (daysEl) daysEl.textContent = serDays;
    if (hoursEl) hoursEl.textContent = serHour;
    if (minutesEl) minutesEl.textContent = serMin;
    if (secondsEl) secondsEl.textContent = serSec;
  }

  stopCountdown() {
    if (this.timerId) {
      clearInterval(this.timerId);
      this.timerId = null;
    }

    const container = this.querySelector('.ug-comp-countdown-div');
    if (typeof this.stopAfterCallbackFn === 'function') {
      this.stopAfterCallbackFn();
    } else if (container) {
      container.classList.add('dpn');
    }
  }

  disconnectedCallback() {
    if (this.timerId) {
      clearInterval(this.timerId);
    }
  }
}

if (!customElements.get('ug-comp-countdown')) {
  customElements.define('ug-comp-countdown', UGCompCountdown);
}
